import { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';
import {
  ComponentsPlatformService,
  IVirtualManifestBuilderProps,
  VirtualManifestAPI,
} from '@wix/editor-platform-manifest-services';

import { loadPanelService } from '../automaticPanel/panelsService';
import { AuthenticationStrategy, Host, WixClient } from '@wix/sdk';
import { reactElements, elements, info } from '@wix/editor';
import {
  CssCustomPropertyItem,
  CssPropertyItem,
  CssPropertyEnumCssPropertyType,
} from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { CompRef, StyleVariableValueValue } from '@wix/document-services-types';
import { GroupingStrategy } from './common/grouping/types';
import {
  CssCustomProperties,
  CssProperties,
} from '@wix/component-protocol/schema';
import { StyleMetadataEngine } from './common/abstractStyles';
import { STYLE_NAMESPACE } from '../../apis/services/editActionsServices/nativeActions/globalDesign/types';
import type {
  ElementStyles,
  ElementStyleDefinitions,
  ElementStylesUpdate,
} from '@wix/public-editor-platform-interfaces';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export type DefaultClient = WixClient<Host, AuthenticationStrategy<Host>> & {
  /** @deprecated use `reactElements` instead */
  element: typeof reactElements;
  reactElements: typeof reactElements;
  info: typeof info;
};

export type { StylesDerivedNativeActionNames };
export type ShadowStyleValue = Extract<
  StyleVariableValueValue,
  { type: 'BoxShadow' }
>;
export type SpacingStyleValue = Extract<
  StyleVariableValueValue,
  { type: 'Length' }
>;

export type CoordinatesStyleValue = Extract<
  StyleVariableValueValue,
  { type: 'Coordinates' }
>;

export type GapShorthandValue = Extract<
  StyleVariableValueValue,
  { type: 'Gap' }
>;

export type Styles = {
  cssCustomProperties: CssCustomProperties;
  cssProperties: CssProperties;
};

export type StyleValues = {
  cssCustomProperties: Record<string, string>;
  cssProperties: Record<string, string>;
};

export type CssPropertyType = keyof typeof CssPropertyEnumCssPropertyType;
export type StyleDefinition = {
  cssPropertyType: CssPropertyType;
  defaultValue?: string;
};

export type GlobalDesignGroupActionPanel = React.ComponentType<{
  host: Host;
  componentsAPI: ComponentsPlatformService;
  stylePresets?: ActionStylePreset[];
  onPresetApplied?: (presetId: string) => void;
  group: GlobalDesignStyleGroup;
}>;

export type GlobalDesignDefaultActionPanel = React.ComponentType<{
  hosts: { host: Host; metadata: HostMetadata }[];
}>;

export type GlobalDesignActionLobbyItem = React.ComponentType<{
  actionName: StylesDerivedNativeActionNames;
  group: GlobalDesignStyleGroup;
  onSelect: () => void;
  componentsAPI: ComponentsPlatformService;
}>;

export type ElementsSDKClient = WixClient<
  any,
  AuthenticationStrategy<any>,
  {
    /** @deprecated use `reactElements` instead */
    element: typeof reactElements;
    reactElements: typeof reactElements;
    elements: typeof elements;
  }
>;

export enum ComparisonMode {
  DirectProps = 'direct-props',
  ValueSet = 'value-set',
}

export type CssCustomPropertyGroupStyleItem = {
  styleItemId: string;
  styleItem: CssCustomPropertyItem;
};

export type CssPropertyGroupStyleItem = {
  styleItemId: string;
  styleItem: CssPropertyItem;
};

export type GroupStylesInfo =
  | {
      styleNamespace: typeof STYLE_NAMESPACE.CssCustomProperties;
      items: CssCustomPropertyGroupStyleItem[];
    }
  | {
      styleNamespace: typeof STYLE_NAMESPACE.CssProperties;
      items: CssPropertyGroupStyleItem[];
    };

/**
 * StyleMetadata - Internal type used by StyleMetadataEngine.
 * Not meant to be stored on groups - use the engine instead.
 */
export type StyleMetadata = {
  styleDefinitions: Styles;
  styles: StyleValues;
};

export type LeadingStyleGroup = StylesGroup & {
  subsetGroups: StylesGroup[];
  comparisonMode: ComparisonMode;
};
// TODO: Unitfy types, only 1 type is needed, remove leadiong and GlobalDesignStyleGroup
export type StylesGroup = {
  displayName: {
    group: string | null;
    component: string;
  };
  editorPointer: EditorPointer<CompRef>;
  context: VirtualManifestAPI;
  isExcluedFromMerging?: boolean;
  engine: StyleMetadataEngine;
} & GroupStylesInfo;

export enum ManifestProcessingStrategy {
  Root = 'root',
  Recursive = 'recursive',
}

export type TargetComponents = {
  leadingGroupInfo: {
    groupId: string;
    displayName: string;
    componentType?: string;
  };
  subsetGroupInfos: {
    groupId: string;
    displayName: string;
  }[];
};

export type HostMetadata = {
  rootDisplayName: string;
  extensionType: string;
  editorPointerId: string;
};

export type GlobalDesignStyleGroupMetadata = {
  /** True if brand colors should be shown in color picker (false when cssCustomProperties namespace is used) */
  isBrandColorSupported: boolean;
  targetComponents?: TargetComponents;
  /** Leading component of the group; passed to the editor's text-theme resolver. */
  editorPointer?: EditorPointer<CompRef>;
};

export type GlobalDesignStyleGroup = {
  displayName: {
    group: string | null;
    component: string;
  };
  subsetGroups: StylesGroup[];
  createHost: () => Promise<Host>;
  /** StyleMetadataEngine is the single source of truth for style data */
  engine?: StyleMetadataEngine;
  /** Metadata flags computed during grouping phase */
  metadata: GlobalDesignStyleGroupMetadata;
};

export enum GlobalDesignActionType {
  Group = 'group',
  Default = 'default',
}

export type GlobalDesignDefaultAction = {
  type: GlobalDesignActionType.Default;
  panel: GlobalDesignDefaultActionPanel;
  getHosts: (
    actionName: StylesDerivedNativeActionNames,
    editorPointers: EditorPointer<CompRef>[],
    getHost: GlobalDesignPanelProps['getHost'],
    componentsAPI: GlobalDesignPanelProps['componentsAPI'],
  ) => Promise<{ host: Host; metadata: HostMetadata }[]>;
};

export type GlobalDesignAction =
  | GlobalDesignGroupAction
  | GlobalDesignDefaultAction;

export type GroupDisplayFilter = (
  groups: StylesGroup[],
  rootContexts: VirtualManifestAPI[],
) => StylesGroup[];

export type ValueSetValueFilter = (args: {
  propertyType: string;
  value: string;
}) => boolean;

export type GlobalDesignGroupAction = {
  type: GlobalDesignActionType.Group;
  panel: GlobalDesignGroupActionPanel;
  lobbyItem: GlobalDesignActionLobbyItem;
  getManifestProcessingStrategy?: (
    context: VirtualManifestAPI,
  ) => ManifestProcessingStrategy;
  groupDisplayFilter?: GroupDisplayFilter;
  comparisonMode: ComparisonMode;
  /**
   * Optional filter for ValueSet grouping/subset detection.
   * When provided, it is applied while extracting the group's value-set.
   *
   * Intended use case: ignore "meaningless" values (e.g. border widths of 0/0px/none/etc.)
   * during ValueSet comparisons.
   */
  valueSetValueFilter?: ValueSetValueFilter;
  grouping: {
    getGroupingStrategy: (
      editorPointers: EditorPointer<CompRef>[],
      getHost: GlobalDesignPanelProps['getHost'],
      componentsAPI: GlobalDesignPanelProps['componentsAPI'],
    ) => GroupingStrategy;
  };
};

export type ActionStylePreset = {
  id: string;
  thumbnail: React.ComponentType<{ isSelected: boolean; onClick: () => void }>;
  values: Record<string, any>;
};

export type GlobalDesignPanelProps = {
  editorPointers: EditorPointer<CompRef>[];
  actionName: StylesDerivedNativeActionNames;
  getHost: (props?: IVirtualManifestBuilderProps) => Promise<Host>;
  componentsAPI: ComponentsPlatformService;
  stylePresets?: ActionStylePreset[];
  t: (key: string, options?: any) => string;
};

export type ControlService = {
  getControl: Awaited<ReturnType<typeof loadPanelService>>['getControl'];
  getControlGroup: Awaited<
    ReturnType<typeof loadPanelService>
  >['getControlGroup'];
  styles: ElementStyles;
  styleDefinitions: ElementStyleDefinitions;
  setStyles: (styles: ElementStylesUpdate) => void;
};
